import { useInPanel } from '@/components/layout/page-panel';
import { cn } from '@/lib/cn';
import { RowSkeleton } from './states';
import { Skeleton, Spinner } from './ui/misc';

/**
 * Loading shapes for each kind of page, drawn the moment a page opens (while its code and data
 * load), so nothing ever waits on a blank screen or a spinner. Each matches its page's layout.
 */

const range = (n: number) => Array.from({ length: n }, (_, i) => i);

/** The featured carousel at the top of Home, Movies and TV Shows. */
export function HeroSkeleton() {
  return (
    <div
      className="relative h-[78vh] min-h-[520px] max-h-[880px] w-full overflow-hidden bg-card/40"
      aria-hidden
    >
      <div className="absolute inset-0 bg-gradient-to-t from-bg via-bg/40 to-transparent" />
      <div className="relative mx-auto flex h-full max-w-[1600px] flex-col justify-end px-4 pb-32 sm:px-10 sm:pb-40">
        <Skeleton className="h-4 w-36 rounded-md" />
        <Skeleton className="mt-4 h-14 w-[min(32rem,80%)] rounded-xl" />
        <Skeleton className="mt-4 h-4 w-60 rounded-md" />
        <Skeleton className="mt-5 h-4 w-[min(40rem,90%)] rounded-md" />
        <Skeleton className="mt-2 h-4 w-[min(34rem,80%)] rounded-md" />
        <div className="mt-7 flex gap-3">
          <Skeleton className="h-12 w-36 rounded-2xl" />
          <Skeleton className="h-12 w-48 rounded-2xl" />
        </div>
      </div>
    </div>
  );
}

/** Home, Movies and TV Shows: the hero and the first rows. */
export function FeedSkeleton() {
  return (
    <div role="status" aria-label="Loading">
      <HeroSkeleton />
      <div className="relative z-10 -mt-24 space-y-8 sm:-mt-28 sm:space-y-10">
        <RowSkeleton />
        <RowSkeleton />
        <RowSkeleton />
      </div>
    </div>
  );
}

/** The title and subtitle of a page (PageHeader's layout). */
function HeaderSkeleton({ subtitle = true }: { subtitle?: boolean }) {
  const inPanel = useInPanel();
  return (
    <div
      className={
        inPanel
          ? 'mx-auto max-w-[1600px] px-4 pb-6 pr-16 pt-10 sm:px-10 sm:pr-20'
          : 'mx-auto max-w-[1600px] px-4 pb-6 pt-24 sm:px-10 lg:pt-28'
      }
    >
      <Skeleton className="h-10 w-56 rounded-xl" />
      {subtitle && <Skeleton className="mt-3 h-4 w-80 max-w-full rounded-md" />}
    </div>
  );
}

/** A grid of posters, as on Discover, Search, My Library and collections. */
export function PosterGridSkeleton({ count = 18 }: { count?: number }) {
  return (
    <ul
      aria-hidden
      className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-x-3 gap-y-7 sm:grid-cols-[repeat(auto-fill,minmax(168px,1fr))] sm:gap-x-4"
    >
      {range(count).map((i) => (
        <li key={i}>
          <Skeleton className="aspect-[2/3]" />
        </li>
      ))}
    </ul>
  );
}

/** A page with a title, a row of filters and a poster grid. */
export function GridPageSkeleton() {
  return (
    <div role="status" aria-label="Loading">
      <HeaderSkeleton />
      <div className="mx-auto max-w-[1600px] px-4 sm:px-10">
        <div className="mb-8 flex gap-2">
          {range(5).map((i) => (
            <Skeleton key={i} className="h-10 w-28 rounded-xl" />
          ))}
        </div>
        <PosterGridSkeleton />
      </div>
    </div>
  );
}

/** A page with a title and a list of wide rows (Watch History, Downloads, Addons). */
export function ListPageSkeleton() {
  return (
    <div role="status" aria-label="Loading">
      <HeaderSkeleton />
      <div className="mx-auto max-w-[1600px] space-y-3 px-4 sm:px-10">
        {range(5).map((i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    </div>
  );
}

/** A film or show (DetailHero's layout): backdrop, poster, title, details and buttons. */
export function DetailSkeleton() {
  const inPanel = useInPanel();
  return (
    <div role="status" aria-label="Loading" className="relative">
      <div className="absolute inset-x-0 top-0 h-[70vh] max-h-[760px] bg-gradient-to-b from-card/60 to-transparent" />
      <div
        className={cn(
          'relative mx-auto flex max-w-[1600px] flex-col gap-8 px-4 pb-10 sm:px-10 md:flex-row md:items-start md:gap-10',
          inPanel ? 'pt-24 md:pt-20' : 'pt-[18vh] md:pt-[clamp(6.5rem,15vh,11rem)]',
        )}
      >
        <Skeleton className="hidden aspect-[2/3] w-52 shrink-0 rounded-[var(--radius-panel)] md:block lg:w-60" />
        <div className="min-w-0 max-w-3xl flex-1">
          <Skeleton className="h-14 w-[min(30rem,90%)] rounded-xl" />
          <div className="mt-5 flex gap-3">
            <Skeleton className="h-5 w-14 rounded-md" />
            <Skeleton className="h-5 w-20 rounded-md" />
            <Skeleton className="h-5 w-16 rounded-md" />
          </div>
          <div className="mt-6 space-y-2.5">
            <Skeleton className="h-4 w-full rounded-md" />
            <Skeleton className="h-4 w-[92%] rounded-md" />
            <Skeleton className="h-4 w-[70%] rounded-md" />
          </div>
          <div className="mt-8 flex gap-3">
            <Skeleton className="h-13 w-40 rounded-2xl" />
            <Skeleton className="h-13 w-44 rounded-2xl" />
            <Skeleton className="h-13 w-13 rounded-2xl" />
          </div>
        </div>
      </div>
      <div className="mx-auto mt-6 max-w-[1600px] px-4 sm:px-10">
        <Skeleton className="h-6 w-24 rounded-lg" />
        <div className="mt-4 flex gap-5 overflow-hidden">
          {range(8).map((i) => (
            <div key={i} className="flex w-24 shrink-0 flex-col items-center gap-2">
              <Skeleton className="h-24 w-24 rounded-full" />
              <Skeleton className="h-3 w-16 rounded" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** The body of a settings page: a couple of cards with setting rows. */
export function SettingsSectionSkeleton() {
  return (
    <div role="status" aria-label="Loading" className="space-y-6">
      {[4, 3].map((rows, s) => (
        <div key={s} className="surface p-6">
          <Skeleton className="h-6 w-40 rounded-lg" />
          <Skeleton className="mt-2 h-4 w-72 max-w-full rounded-md" />
          <div className="mt-6 space-y-5">
            {range(rows).map((i) => (
              <div key={i} className="flex items-center justify-between gap-6">
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-44 rounded-md" />
                  <Skeleton className="h-3 w-64 max-w-full rounded" />
                </div>
                <Skeleton className="h-7 w-12 rounded-full" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Settings as a whole (SettingsLayout's layout): the title, its sidebar and a section. */
export function SettingsSkeleton() {
  const inPanel = useInPanel();
  return (
    <div
      className={cn(
        'mx-auto max-w-[1400px] px-4 pb-10 sm:px-10',
        inPanel ? 'pt-10' : 'pt-24 lg:pt-28',
      )}
    >
      <Skeleton className="h-10 w-40 rounded-xl" />
      <div className="mt-8 flex flex-col gap-8 lg:flex-row">
        <div className="hidden space-y-2 lg:block lg:w-60 lg:shrink-0" aria-hidden>
          {range(11).map((i) => (
            <Skeleton key={i} className="h-10 rounded-xl" />
          ))}
        </div>
        <div className="min-w-0 flex-1">
          <SettingsSectionSkeleton />
        </div>
      </div>
    </div>
  );
}

/** Friends (its page layout): the header, people, tabs and activity cards. */
export function FriendsSkeleton({ bare }: { bare?: boolean }) {
  const inPanel = useInPanel();
  const body = (
    <div role="status" aria-label="Loading">
      <div className="flex items-end justify-between gap-4">
        <div>
          <Skeleton className="h-10 w-40 rounded-xl" />
          <Skeleton className="mt-3 h-4 w-64 rounded-md" />
        </div>
        <Skeleton className="h-11 w-36 rounded-2xl" />
      </div>
      <div className="mt-8 flex gap-5 overflow-hidden">
        {range(6).map((i) => (
          <div key={i} className="flex w-20 shrink-0 flex-col items-center gap-2">
            <Skeleton className="h-16 w-16 rounded-full" />
            <Skeleton className="h-3 w-14 rounded" />
          </div>
        ))}
      </div>
      <div className="mb-6 mt-10 flex gap-3 border-b border-line pb-3">
        {range(3).map((i) => (
          <Skeleton key={i} className="h-6 w-28 rounded-lg" />
        ))}
      </div>
      <ActivitySkeleton />
    </div>
  );
  if (bare) return body;
  return (
    <div
      className={cn(
        'mx-auto max-w-[1400px] px-4 pb-16 sm:px-10',
        inPanel ? 'pr-16 pt-10 sm:pr-20' : 'pt-24 lg:pt-28',
      )}
    >
      {body}
    </div>
  );
}

/** Friends' activity cards. */
export function ActivitySkeleton() {
  return (
    <ul aria-hidden className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
      {range(6).map((i) => (
        <li key={i} className="surface flex gap-4 p-4">
          <Skeleton className="aspect-[2/3] w-16 shrink-0 rounded-lg" />
          <div className="flex-1 space-y-2.5 pt-1">
            <div className="flex items-center gap-2">
              <Skeleton className="h-7 w-7 rounded-full" />
              <Skeleton className="h-3.5 w-24 rounded" />
            </div>
            <Skeleton className="h-4 w-[80%] rounded-md" />
            <Skeleton className="h-3 w-[55%] rounded" />
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Profile statistics: summary tiles and charts. */
export function StatsSkeleton() {
  return (
    <div role="status" aria-label="Loading">
      <HeaderSkeleton />
      <div className="mx-auto max-w-[1600px] space-y-6 px-4 sm:px-10">
        <Skeleton className="h-44" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {range(4).map((i) => (
            <Skeleton key={i} className="h-36" />
          ))}
        </div>
      </div>
    </div>
  );
}

/** The player while it opens: black, like the video it becomes. */
export function PlayerSkeleton() {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black">
      <Spinner className="h-10 w-10" label="Loading player" />
    </div>
  );
}

/** Full-screen pages outside the app (welcome, sign in, setup): their background, at once. */
export function ScreenSkeleton() {
  return <div className="ambient min-h-dvh" aria-hidden />;
}
